import { BRAND } from '../../config/branding';
import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { ArrowLeft, Shield, FileText, CheckCircle } from 'lucide-react';

export default function PrivacyPage() {
  usePageMeta({
    title: 'Privacy Policy – Stratel',
    description: `Learn how ${BRAND.name} collects, uses, and protects personal information for website visitors, clients, and partners.`,
    keywords: 'privacy policy, data privacy, Stratel, personal information, security',
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-600 mt-1">How {BRAND.name} handles personal information and protects your privacy.</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2>Introduction</h2>
              <p>
                {BRAND.name} collects and processes personal information to deliver services, support inquiries, and operate our website.
                We are committed to keeping data secure, using it responsibly, and giving visitors and clients control over their information.
              </p>
            </section>

            <section>
              <h2>Information We Collect</h2>
              <ul>
                <li>Contact details you submit through forms, such as name, email address, phone number, and organization.</li>
                <li>Account details and credentials when you register for client access or training.</li>
                <li>Usage data from website visits, including page views, device information, and browsing behavior.</li>
                <li>Communications you send us through contact forms, email, or support requests.</li>
              </ul>
            </section>

            <section>
              <h2>How We Use Personal Information</h2>
              <ul>
                <li>To respond to inquiries, provide services, and manage client relationships.</li>
                <li>To administer accounts, deliver training, and support platform access.</li>
                <li>To improve our website, products, and services using aggregated usage insights.</li>
                <li>To comply with legal obligations and protect the rights and safety of our clients and operations.</li>
              </ul>
            </section>

            <section className="grid gap-6 md:grid-cols-2">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900 m-0">Data Retention</h3>
                </div>
                <p className="text-blue-700 m-0">
                  We retain personal data only as long as necessary to fulfill the purposes described in this policy, or as required by law.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-900 m-0">Your Choices</h3>
                </div>
                <p className="text-green-700 m-0">
                  You can request access, correction, or deletion of your personal information by contacting our team at support@stratelplus.com.
                </p>
              </div>
            </section>

            <section>
              <h2>Security</h2>
              <p>
                We use administrative, technical, and physical safeguards to protect information. Access is limited to authorized personnel, and we regularly review our security practices.
              </p>
            </section>

            <section>
              <h2>Third-Party Services</h2>
              <p>
                We may use trusted third-party service providers to support website operations, analytics, and communications. Those providers are contractually required to protect personal data and use it only for permitted purposes.
              </p>
            </section>

            <section>
              <h2>Contact Us</h2>
              <p>
                For questions about this policy or your privacy rights, email us at{' '}
                <a href="mailto:support@stratelplus.com" className="text-blue-600 hover:underline">support@stratelplus.com</a>.
              </p>
            </section>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          Last updated: April 2026
        </p>
      </div>
    </div>
  );
}
